import { supabase } from "@/integrations/supabase/client";

export type ErrorReport = {
  message: string;
  stack?: string;
  severity?: "error" | "warning" | "info";
  flow?: string;
  route?: string;
  context?: Record<string, unknown>;
  user_role?: string;
  club_id?: string;
};

let lastFingerprint = "";
let lastTimestamp = 0;

/**
 * Send a real-data error to the centralised log so super-admins can see it on
 * the Test & Debug page. Failures are swallowed — logging must never break the
 * UX. A simple in-memory dedupe avoids spamming the same error twice within 5s.
 */
export async function logRealtimeError(report: ErrorReport): Promise<void> {
  try {
    const fp = `${report.flow ?? ""}|${report.message}`;
    const now = Date.now();
    if (fp === lastFingerprint && now - lastTimestamp < 5000) return;
    lastFingerprint = fp;
    lastTimestamp = now;

    const route =
      report.route ?? (typeof window !== "undefined" ? window.location.pathname : undefined);

    await supabase.functions.invoke("log-error", {
      body: { ...report, route },
    });
  } catch {
    // Never let logging break the app
  }
}

/** Install global handlers for uncaught errors / promise rejections. */
export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;
  if ((window as any).__husselErrorHandlersInstalled) return;
  (window as any).__husselErrorHandlersInstalled = true;

  window.addEventListener("error", (event) => {
    void logRealtimeError({
      flow: "window.error",
      message: event.message || "Unknown window error",
      stack: event.error?.stack,
      severity: "error",
      context: {
        filename: event.filename,
        line: event.lineno,
        col: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason: any = event.reason;
    void logRealtimeError({
      flow: "unhandledrejection",
      message: typeof reason === "string" ? reason : reason?.message ?? "Unhandled promise rejection",
      stack: reason?.stack,
      severity: "error",
    });
  });
}
